import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import {
  DeactivateInvoiceFeePayload,
  InvoiceFee,
  InvoiceFeePayload,
  InvoiceSiteConfig,
  InvoiceSiteConfigPayload,
} from '../../models/invoice-settings/invoice-settings.model';

@Injectable({ providedIn: 'root' })
export class MapperInvoiceSettingsService {
  mapFeeList(dtos: InvoiceFee[] | null | undefined): InvoiceFee[] {
    if (!dtos?.length) return [];
    return dtos.map((item) => this.mapFee(item));
  }

  mapFee(item: InvoiceFee | Record<string, unknown>): InvoiceFee {
    const raw = item as Record<string, unknown>;
    const src = item as InvoiceFee;
    return {
      id: Number(src.id ?? raw['Id'] ?? 0),
      name: String(src.name ?? raw['Name'] ?? ''),
      feeId: Number(src.feeId ?? raw['FeeId'] ?? 0),
      feeName: String(src.feeName ?? raw['FeeName'] ?? ''),
      fee: Number(src.fee ?? raw['Fee'] ?? 0),
      vat: Boolean(src.vat ?? raw['Vat'] ?? false),
      feeTypeId: (src.feeTypeId ?? raw['FeeTypeId']) != null ? Number(src.feeTypeId ?? raw['FeeTypeId']) : null,
      feeTypeName: String(src.feeTypeName ?? raw['FeeTypeName'] ?? ''),
      status: Boolean(src.status ?? raw['Status'] ?? true),
      sortOrder: Number(src.sortOrder ?? raw['SortOrder'] ?? 0),
      siteId: (src.siteId ?? raw['SiteId']) != null ? Number(src.siteId ?? raw['SiteId']) : null,
      siteName: String(src.siteName ?? raw['SiteName'] ?? ''),
      description: String(src.description ?? raw['Description'] ?? ''),
    };
  }

  mapSiteConfig(dto: InvoiceSiteConfig | Record<string, unknown>): InvoiceSiteConfig {
    const raw = dto as Record<string, unknown>;
    const src = dto as InvoiceSiteConfig;
    return {
      id: Number(src.id ?? raw['Id'] ?? 0),
      siteId: Number(src.siteId ?? raw['SiteId'] ?? 0),
      siteName: String(src.siteName ?? raw['SiteName'] ?? ''),
      defaultInvoiceType: String(src.defaultInvoiceType ?? raw['DefaultInvoiceType'] ?? 'billing'),
      enableThaiQr: Boolean(src.enableThaiQr ?? raw['EnableThaiQr'] ?? true),
      enableBankTransfer: Boolean(src.enableBankTransfer ?? raw['EnableBankTransfer'] ?? true),
      enableStaffConfirm: Boolean(src.enableStaffConfirm ?? raw['EnableStaffConfirm'] ?? true),
      defaultBankAccountId:
        (src.defaultBankAccountId ?? raw['DefaultBankAccountId']) != null
          ? Number(src.defaultBankAccountId ?? raw['DefaultBankAccountId'])
          : null,
      defaultBankAccountName: String(src.defaultBankAccountName ?? raw['DefaultBankAccountName'] ?? ''),
      allowBankTransferNotify: Boolean(src.allowBankTransferNotify ?? raw['AllowBankTransferNotify'] ?? true),
      externalNotifyUrl: String(src.externalNotifyUrl ?? raw['ExternalNotifyUrl'] ?? ''),
      defaultDueDays: Number(src.defaultDueDays ?? raw['DefaultDueDays'] ?? 30),
      invoiceNoPrefix: String(src.invoiceNoPrefix ?? raw['InvoiceNoPrefix'] ?? 'IV'),
      status: Boolean(src.status ?? raw['Status'] ?? true),
    };
  }

  mapFeeToPayload(
    fee: Partial<InvoiceFee>,
    userId: number,
    menuId: number,
    siteId?: number | null
  ): InvoiceFeePayload {
    return {
      userId,
      menuId,
      name: fee.name?.trim() ?? '',
      feeId: Number(fee.feeId ?? 0),
      fee: fee.fee ?? 0,
      vat: Boolean(fee.vat),
      feeTypeId: Number(fee.feeTypeId ?? 0),
      siteId: siteId ?? fee.siteId ?? null,
      sortOrder: Number(fee.sortOrder ?? 0),
      description: fee.description ?? '',
      status: fee.status ?? true,
    };
  }

  mapSiteConfigToPayload(
    config: InvoiceSiteConfig,
    userId: number,
    menuId: number
  ): InvoiceSiteConfigPayload {
    return {
      userId,
      menuId,
      siteId: config.siteId,
      defaultInvoiceType: config.defaultInvoiceType || 'billing',
      enableThaiQr: config.enableThaiQr,
      enableBankTransfer: config.enableBankTransfer,
      enableStaffConfirm: config.enableStaffConfirm,
      defaultBankAccountId: config.defaultBankAccountId,
      allowBankTransferNotify: config.allowBankTransferNotify,
      externalNotifyUrl: config.externalNotifyUrl || null,
      defaultDueDays: config.defaultDueDays,
      invoiceNoPrefix: config.invoiceNoPrefix || 'IV',
      status: config.status,
    };
  }

  mapDeactivatePayload(userId: number, menuId: number): DeactivateInvoiceFeePayload {
    return { userId, menuId };
  }

  mapActionResponse(res: unknown): ActionBackOfficeResponse {
    const raw = (res ?? {}) as Record<string, unknown>;
    return {
      isSuccess: Boolean(raw['isSuccess'] ?? raw['IsSuccess'] ?? false),
      message: String(raw['message'] ?? raw['Message'] ?? ''),
      messageEn: String(raw['messageEn'] ?? raw['MessageEn'] ?? ''),
      code: String(raw['code'] ?? raw['Code'] ?? ''),
      IdReturn: Number(raw['IdReturn'] ?? raw['idReturn'] ?? 0),
    };
  }
}
